import { UtensilsCrossed, BedDouble, Store } from 'lucide-react';
import { useLang } from '../i18n';
import SEO from '../components/SEO';
import { LineReveal, FadeIn, ParallaxImage } from '../components/motion';
import ContactForm from '../components/ContactForm';

const HERO_IMG = 'https://images.pexels.com/photos/17057034/pexels-photo-17057034.jpeg?auto=compress&cs=tinysrgb&w=1600';

const PrivateLabel = () => {
  const { t } = useLang();
  const sectors = [
    { icon: UtensilsCrossed, t: t('pl.s1'), d: t('pl.s1d') },
    { icon: BedDouble, t: t('pl.s2'), d: t('pl.s2d') },
    { icon: Store, t: t('pl.s3'), d: t('pl.s3d') },
  ];
  const steps = t('pl.steps');

  return (
    <main data-testid="private-label-page">
      <SEO page="private-label" />
      <section className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden bg-[#22303C] text-[#F7F5F0] noise-overlay pt-24" data-testid="pl-hero">
        <ParallaxImage src={HERO_IMG} alt="Fine dining" speed={0.12} className="absolute inset-0" imgClassName="opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#22303C] via-[#22303C]/50 to-[#22303C]/20" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 w-full pb-16 md:pb-24">
          <FadeIn y={16} className="mb-8">
            <span className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary">
              <span className="w-10 h-px bg-secondary inline-block" />
              {t('pl.eyebrow')}
            </span>
          </FadeIn>
          <LineReveal
            lines={[
              <span key="1">{t('pl.heroLine1')}</span>,
              <span key="2" className="font-serif italic font-light text-secondary">{t('pl.heroLine2')}</span>,
            ]}
            lineClassName="font-display font-extrabold text-[12vw] md:text-[9vw] leading-[0.88] tracking-tighter"
            className="mb-10"
          />
          <FadeIn delay={0.5} className="max-w-xl">
            <p className="text-base md:text-lg text-white/70 leading-relaxed" data-testid="pl-hero-sub">{t('pl.heroSub')}</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="pl-sectors-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <FadeIn className="mb-14">
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="pl-sectors-title">{t('pl.sectors')}</h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 border-t border-l border-foreground/10">
            {sectors.map((s, i) => (
              <FadeIn key={i} delay={i * 0.1} className="border-b border-r border-foreground/10 p-8 md:p-12 group hover:bg-muted/60 transition-colors duration-500">
                <s.icon className="w-8 h-8 text-secondary mb-8" strokeWidth={1.25} />
                <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mb-4" data-testid={`pl-sector-title-${i}`}>{s.t}</h3>
                <p className="text-foreground/60 leading-relaxed text-sm md:text-base">{s.d}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="dark-section bg-[#22303C] text-[#F7F5F0] noise-overlay py-24 md:py-32" data-testid="pl-steps-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <FadeIn className="mb-14">
            <h2 className="font-serif italic font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="pl-steps-title">{t('pl.stepsTitle')}</h2>
          </FadeIn>
          <div>
            {steps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.05} className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 md:py-12 border-t border-white/10 first:border-t-0 first:pt-0 items-baseline">
                <div className="md:col-span-2">
                  <span className="font-serif italic font-light text-6xl md:text-7xl text-secondary/80 leading-none" data-testid={`pl-step-number-${s.n}`}>{s.n}</span>
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">{s.t}</h3>
                </div>
                <div className="md:col-span-6">
                  <p className="text-white/60 leading-relaxed">{s.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="pl-form-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <FadeIn>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="pl-form-title">{t('pl.formTitle')}</h2>
              <p className="text-foreground/60 text-base md:text-lg mt-6 max-w-md">{t('pl.formSub')}</p>
            </FadeIn>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <FadeIn delay={0.15}>
              <ContactForm defaultTopic="private-label" testIdPrefix="pl-contact" />
            </FadeIn>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivateLabel;
